import { MarketViewDiscipline } from '../../services/api'

/**
 * Resolve flat tech IDs from search response to leaf IDs using the full discipline tree.
 *
 * The search response `tech_tree` returns flat technology objects (no `children`),
 * which may include parent node IDs. The TechnologyTreeFilter expects only leaf IDs
 * in the selected set, since parent state is derived from descendants.
 *
 * - If a result ID is a leaf in the full tree, keep it.
 * - If it's a parent node, expand to all its leaf descendants.
 * - If it's not found (e.g. full tree not loaded yet), keep it as-is.
 */
export function resolveToLeafIds(
  resultTechIds: number[],
  fullDisciplines: MarketViewDiscipline[],
): number[] {
  // Build a map: nodeId → TechnologyNode from the full tree
  const nodeMap = new Map<number, { id: number; children?: any[] }>()
  function index(node: { id: number; children?: any[] }) {
    nodeMap.set(node.id, node)
    if (node.children) node.children.forEach(index)
  }
  for (const d of fullDisciplines) {
    for (const t of d.technologies) index(t)
  }

  // Collect leaves for each result tech ID
  const leafIds = new Set<number>()
  function collectLeaves(node: { id: number; children?: any[] }) {
    if (!node.children || node.children.length === 0) {
      leafIds.add(node.id)
    } else {
      node.children.forEach(collectLeaves)
    }
  }

  for (const id of resultTechIds) {
    const node = nodeMap.get(id)
    if (node) {
      collectLeaves(node)
    } else {
      // Not in full tree (maybe tree not loaded yet) — keep raw ID
      leafIds.add(id)
    }
  }

  return Array.from(leafIds)
}
